import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { TEMPERATURA_INFO, ESTAGIO_RELACIONAMENTO_LABELS, RelacionamentoQuente } from '@/pages/painel/lib/inteligencia';

export default function RelacionamentosImperdiveis({ password }: { password: string }) {
  const [itens, setItens] = useState<RelacionamentoQuente[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data: resp, error: invokeErr } = await supabase.functions.invoke('relacionamento-admin', {
          body: { action: 'relacionamentos_quentes', password },
        });
        if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
        if (resp?.error) throw new Error(resp.error);
        if (ativo) {
          const lista = (resp?.relacionamentos ?? []) as RelacionamentoQuente[];
          // prioriza "sem próxima ação" e limita a 5 (qualidade, não quantidade)
          const semAcao = lista.filter((r) => r.sem_proxima_acao);
          const comAcao = lista.filter((r) => !r.sem_proxima_acao);
          setItens([...semAcao, ...comAcao].slice(0, 5));
        }
      } catch (e) {
        if (ativo) setError(e instanceof Error ? e.message : 'Erro ao carregar.');
      }
    })();
    return () => { ativo = false; };
  }, [password]);

  if (itens.length === 0 && !error) return null;

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600">
            <i className="ri-heart-3-line"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Relacionamentos que não posso perder</h3>
            <p className="text-xs text-gray-400">Portas abertas que precisam de um próximo passo.</p>
          </div>
        </div>
        <Link to="/painel/relacionamentos" className="text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap">
          Ver todos<i className="ri-arrow-right-line ml-1"></i>
        </Link>
      </div>

      {error ? (
        <p className="text-xs text-gray-400">Indisponível no momento.</p>
      ) : itens.length === 0 ? (
        <p className="text-xs text-gray-400">Nenhum relacionamento quente sem próximo passo.</p>
      ) : (
        <div className="space-y-2">
          {itens.map((r) => {
            const temp = TEMPERATURA_INFO[r.temperatura];
            const url = r.entidade_tipo === 'contato' ? `/painel/contatos/${r.contato_id}` : `/painel/clientes/${r.empresa_id}`;
            return (
              <div key={`${r.entidade_tipo}:${r.entidade_id}`} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl px-3 py-2.5">
                <div className="min-w-0 flex items-center gap-2.5">
                  <span className={`text-sm shrink-0`}>{temp?.emoji}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{r.nome}{r.alias ? <span className="text-gray-400 font-normal"> “{r.alias}”</span> : null}</p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {r.cidade}{r.uf ? `/${r.uf}` : ''}{r.estagio ? ` · ${ESTAGIO_RELACIONAMENTO_LABELS[r.estagio] ?? r.estagio}` : ''}
                    </p>
                  </div>
                </div>
                {r.sem_proxima_acao ? (
                  <span className="text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 shrink-0 whitespace-nowrap">Sem próxima ação</span>
                ) : (
                  <span className="text-[11px] text-gray-400 shrink-0 whitespace-nowrap">Com próxima ação</span>
                )}
                <Link to={url} className="text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap shrink-0">Abrir</Link>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}